import React, { useEffect, useMemo, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Channel, Company, Contact, Opportunity, PipelineStage, Product, UserMini } from "../types/crm";

interface QuickAddModalProps {
  onClose: () => void;
  onCreated: (opportunity: Opportunity) => void;
}

/**
 * Cadastro rápido (quick add): só os campos essenciais para não perder o lead
 * no calor da conversa. A etapa entra automaticamente como "Primeiro contato"
 * e o card nasce marcado como incompleto (needs_completion) para ser
 * completado depois pelo modal completo.
 */
export function QuickAddModal({ onClose, onCreated }: QuickAddModalProps) {
  const { username } = useAuth();

  const [channels, setChannels] = useState<Channel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<UserMini[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [firstStage, setFirstStage] = useState<PipelineStage | null>(null);

  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [channelId, setChannelId] = useState<number | "">("");
  const [productId, setProductId] = useState<number | "">("");
  const [ownerId, setOwnerId] = useState<number | "">("");

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingData(true);
      setLoadError(null);
      try {
        const [channelsRes, productsRes, usersRes, companiesRes, stagesRes] = await Promise.all([
          api.get<Channel[]>("/channels/"),
          api.get<Product[]>("/products/"),
          api.get<UserMini[]>("/users/"),
          api.get<Company[]>("/companies/"),
          api.get<PipelineStage[]>("/pipeline-stages/"),
        ]);
        if (cancelled) return;
        const activeChannels = channelsRes.data.filter((c) => c.is_active);
        const activeUsers = usersRes.data.filter((u) => u.is_active);
        setChannels(activeChannels);
        setProducts(productsRes.data.filter((p) => p.is_active));
        setUsers(activeUsers);
        setCompanies(companiesRes.data);
        setFirstStage(stagesRes.data.find((s) => s.order === 1) ?? null);
        // Responsável auto-preenchido com o usuário logado (username é único).
        const me = activeUsers.find((u) => u.username === username);
        if (me) setOwnerId(me.id);
      } catch {
        if (!cancelled) setLoadError("Não foi possível carregar os dados do formulário.");
      } finally {
        if (!cancelled) setIsLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [username]);

  // Validação de duplicata: avisa (não bloqueia) se já existe empresa com nome igual.
  const duplicateCompany = useMemo(() => {
    const trimmed = companyName.trim().toLowerCase();
    if (!trimmed) return null;
    return companies.find((c) => c.name.trim().toLowerCase() === trimmed) ?? null;
  }, [companyName, companies]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!companyName.trim()) errors.company = "Informe o nome da empresa";
    if (!contactName.trim()) errors.contact = "Informe o nome do contato";
    if (channelId === "") errors.channel = "Selecione o canal";
    if (ownerId === "") errors.owner = "Selecione o responsável";
    if (!firstStage) errors.stage = "Etapa inicial não encontrada";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setIsSubmitting(true);
    try {
      // Reutiliza empresa existente em caso de nome igual (evita duplicata);
      // caso contrário cria uma nova.
      const companyId = duplicateCompany
        ? duplicateCompany.id
        : (await api.post<Company>("/companies/", { name: companyName.trim(), cnpj: "", website: "" })).data.id;

      const contactId = (
        await api.post<Contact>("/contacts/", {
          company: companyId,
          name: contactName.trim(),
          email: "",
          phone: "",
          role: "",
        })
      ).data.id;

      const response = await api.post<Opportunity>("/opportunities/", {
        company: companyId,
        primary_contact: contactId,
        owner: ownerId,
        channel: channelId,
        stage: firstStage!.id,
        products: productId === "" ? [] : [productId],
        mrr_estimated: 0,
        non_recurring_estimated: 0,
        needs_completion: true,
      });

      onCreated(response.data);
    } catch {
      setFormError("Não foi possível cadastrar o lead. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 py-16">
      <div className="w-full max-w-sm rounded-[10px] border border-border-subtle bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">Cadastro rápido de lead</h2>
        <p className="mt-1 text-sm text-slate-500">
          Só o essencial. Você completa os demais dados depois.
        </p>

        {isLoadingData && <p className="mt-4 text-sm text-slate-500">Carregando...</p>}

        {!isLoadingData && loadError && (
          <p className="mt-4 text-sm text-red-600">{loadError}</p>
        )}

        {!isLoadingData && !loadError && (
          <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="qa_company" className="block text-sm font-medium text-slate-700">
                Empresa
              </label>
              <input
                id="qa_company"
                type="text"
                autoFocus
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {fieldErrors.company && <p className="mt-1 text-sm text-red-600">{fieldErrors.company}</p>}
              {duplicateCompany && !fieldErrors.company && (
                <p className="mt-1 text-xs text-amber-600">
                  Já existe a empresa "{duplicateCompany.name}". O lead será vinculado a ela.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="qa_contact" className="block text-sm font-medium text-slate-700">
                Contato
              </label>
              <input
                id="qa_contact"
                type="text"
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {fieldErrors.contact && <p className="mt-1 text-sm text-red-600">{fieldErrors.contact}</p>}
            </div>

            <div>
              <label htmlFor="qa_channel" className="block text-sm font-medium text-slate-700">
                Canal de origem
              </label>
              <select
                id="qa_channel"
                value={channelId}
                onChange={(event) => setChannelId(event.target.value === "" ? "" : Number(event.target.value))}
                className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Selecione um canal</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name}
                  </option>
                ))}
              </select>
              {fieldErrors.channel && <p className="mt-1 text-sm text-red-600">{fieldErrors.channel}</p>}
            </div>

            <div>
              <label htmlFor="qa_product" className="block text-sm font-medium text-slate-700">
                Produto de interesse <span className="text-slate-400">(opcional)</span>
              </label>
              <select
                id="qa_product"
                value={productId}
                onChange={(event) => setProductId(event.target.value === "" ? "" : Number(event.target.value))}
                className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Nenhum por enquanto</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="qa_owner" className="block text-sm font-medium text-slate-700">
                Responsável
              </label>
              <select
                id="qa_owner"
                value={ownerId}
                onChange={(event) => setOwnerId(event.target.value === "" ? "" : Number(event.target.value))}
                className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Selecione um responsável</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.username}
                  </option>
                ))}
              </select>
              {fieldErrors.owner && <p className="mt-1 text-sm text-red-600">{fieldErrors.owner}</p>}
            </div>

            {fieldErrors.stage && <p className="text-sm text-red-600">{fieldErrors.stage}</p>}
            {formError && <p className="text-sm text-red-600">{formError}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-60"
              >
                {isSubmitting ? "Salvando..." : "Salvar lead"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
